import { Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IHttpResponse, ILang, LANGS_ENUM, TLang } from '@libs/interfaces';
import { catchError, combineLatest, Observable, of, tap } from 'rxjs';
import { registerLocaleData } from '@angular/common';
import localePT from '@angular/common/locales/pt';
import localeEN from '@angular/common/locales/en';
import localeES from '@angular/common/locales/es';
import { Store } from '@ngrx/store';
import { updateUserLang } from '@libs/store/auth/actions';
import { LANGS } from '@libs/helper/lang.helper';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class LangService {
  lang = signal<TLang>('pt');
  constructor(
    public translateService: TranslateService,
    private apiService: ApiService,
    private store: Store,
  ) {}

  /**
   * Initializes the language service by setting up available languages,
   * determining the default language, and applying it to the application.
   */
  init() {
    const langs = this.langs.map(l => l.code);
    this.translateService.addLangs(langs);
    this.lang.set(this.langs.find(l => l.code == this.browserLang).code);
    const code = this.lang() || (this.default?.code as LANGS_ENUM);
    this.translateService.setDefaultLang(code);
    this.use(code);
    document.querySelector('html')?.setAttribute('lang', code);
  }

  /**
   * Sets the current language for the translation service and updates the language signal.
   * @param lang - The language code to set.
   */
  use(lang: TLang) {
    this.translateService.use(lang as string);
    this.lang.set(lang);
    switch (lang) {
      case 'pt':
        registerLocaleData(localePT);
        break;
      case 'en':
        registerLocaleData(localeEN);
        break;
      case 'es':
        registerLocaleData(localeES);
        break;
    }
  }

  /**
   * Translates a given string and returns a promise that resolves with the translated string.
   * @param str - The string to translate.
   * @returns A promise that resolves with the translated string.
   */
  async translate(str: string): Promise<string> {
    return new Promise(resolve => this.translateService.get(str).subscribe(resolve));
  }

  /**
   * Translates a given string and returns an observable that emits the translated string.
   * @param str - The string to translate.
   * @returns An observable that emits the translated string.
   */
  translate$(str: string): Observable<string> {
    return this.translateService.get(str);
  }

  /**
   * Translates an array of strings and returns an observable that emits an array of translated strings.
   * @param arr - The array of strings to translate.
   * @returns An observable that emits an array of translated strings.
   */
  transtaleArr(arr: string[]): Observable<string[]> {
    const subs$ = arr.map((str: string) => this.translate$(str));
    return combineLatest(subs$);
  }

  /**
   * Sets the current language for the user and updates the language in the backend.
   * @param lang - The language code to set.
   * @returns An observable that emits the HTTP response.
   */
  setLang_(lang: LANGS_ENUM): Observable<IHttpResponse> {
    this.use(lang);
    this.lang.set(lang);
    return this.apiService.patch(`user/lang/${lang}`).pipe(
      catchError(() => of({ error: true })),
      tap((response: IHttpResponse) => !response.error && this.store.dispatch(updateUserLang({ lang }))),
    );
  }

  /**
   * Gets the default language from the available languages.
   * @returns The default language.
   */
  get default(): ILang {
    return this.langs.find(l => l.default) || LANGS[1];
  }

  /**
   * Gets the list of available languages.
   * @returns An array of available languages.
   */
  get langs(): any[] {
    return LANGS;
  }

  /**
   * Gets the browser's language.
   * @returns The browser's language code.
   */
  get browserLang(): TLang {
    return navigator.language.substring(0, 2) as LANGS_ENUM;
  }
}
